import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import Preloader from "../../components/Preloader";

const Plans = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("add"); // "add" or "modify"

  // State for "Add Plan"
  const [newPlan, setNewPlan] = useState({
    name: "",
    description: "",
    stripeProductId: "",
    stripePriceId: "",
    role: "student",
    durationUnit: "semester",
    durationCount: 1,
    price: 0,
    currency: "USD",
    features: "",
    isActive: true,
  });

  // State for "Modify Plan"
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [planDetails, setPlanDetails] = useState({
    name: "",
    description: "",
    stripeProductId: "",
    stripePriceId: "",
    role: "student",
    durationUnit: "semester",
    durationCount: 1,
    price: 0,
    currency: "USD",
    features: "",
    isActive: true,
  });

  // Fetch plans
  const fetchPlans = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, "plans"));
      const data = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      setPlans(data);
    } catch (err) {
      console.error("Error fetching plans:", err);
      alert("Failed to fetch plans.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "modify") fetchPlans();
  }, [activeTab]);

  const handleNewPlanChange = e => {
    const { name, value, type, checked } = e.target;
    setNewPlan(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleAddPlan = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const ref = doc(collection(db, "plans"));
      await setDoc(ref, {
        name: newPlan.name,
        description: newPlan.description,
        stripeProductId: newPlan.stripeProductId,
        stripePriceId: newPlan.stripePriceId,
        role: newPlan.role,
        durationUnit: newPlan.durationUnit,
        durationCount: Number(newPlan.durationCount),
        price: Number(newPlan.price),
        currency: newPlan.currency,
        features: newPlan.features.split(",").map(f => f.trim()),
        isActive: newPlan.isActive,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      alert("Plan added successfully.");
      setNewPlan({
        name: "",
        description: "",
        stripeProductId: "",
        stripePriceId: "",
        role: "student",
        durationUnit: "semester",
        durationCount: 1,
        price: 0,
        currency: "USD",
        features: "",
        isActive: true,
      });
    } catch (err) {
      console.error("Error adding plan:", err);
      alert("Failed to add plan.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = async id => {
    setLoading(true);
    try {
      const docRef = doc(db, "plans", id);
      const snap = await getDoc(docRef);
      if (!snap.exists()) return alert("Plan not found.");
      const data = snap.data();
      setPlanDetails({
        name: data.name || "",
        description: data.description || "",
        stripeProductId: data.stripeProductId || "",
        stripePriceId: data.stripePriceId || "",
        role: data.role || "student",
        durationUnit: data.durationUnit || "semester",
        durationCount: data.durationCount || 1,
        price: data.price || 0,
        currency: data.currency || "USD",
        features: data.features ? data.features.join(", ") : "",
        isActive: data.isActive ?? true,
      });
      setSelectedPlanId(id);
    } catch (err) {
      console.error("Error fetching plan details:", err);
      alert("Failed to fetch plan details.");
    } finally {
      setLoading(false);
    }
  };

  const handlePlanDetailChange = e => {
    const { name, value, type, checked } = e.target;
    setPlanDetails(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleUpdatePlan = async e => {
    e.preventDefault();
    if (!selectedPlanId) return alert("No plan selected.");
    setLoading(true);
    try {
      const docRef = doc(db, "plans", selectedPlanId);
      await updateDoc(docRef, {
        name: planDetails.name,
        description: planDetails.description,
        stripeProductId: planDetails.stripeProductId,
        stripePriceId: planDetails.stripePriceId,
        role: planDetails.role,
        durationUnit: planDetails.durationUnit,
        durationCount: Number(planDetails.durationCount),
        price: Number(planDetails.price),
        currency: planDetails.currency,
        features: planDetails.features.split(",").map(f => f.trim()),
        isActive: planDetails.isActive,
        updatedAt: serverTimestamp(),
      });
      alert("Plan updated successfully.");
      fetchPlans();
      setSelectedPlanId("");
      setPlanDetails({
        name: "",
        description: "",
        stripeProductId: "",
        stripePriceId: "",
        role: "student",
        durationUnit: "semester",
        durationCount: 1,
        price: 0,
        currency: "USD",
        features: "",
        isActive: true,
      });
    } catch (err) {
      console.error("Error updating plan:", err);
      alert("Failed to update plan.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="plans-section"
      className="flex flex-col items-center justify-start min-h-screen p-8"
      style={{ backgroundColor: "var(--section-bg)", color: "var(--text-color)" }}
    >
      {loading && <Preloader />}

      {/* Tabs */}
      <ul className="flex border-b border-[var(--border-color)] w-full max-w-4xl mb-4">
        {['add','modify'].map(tab => (
          <li key={tab} className="mr-4">
            <button
              className={`px-4 py-2 -mb-px border-b-2 transition-colors ${activeTab===tab
                ? 'border-[var(--primary-color)] text-[var(--primary-color)]'
                : 'border-transparent hover:border-[var(--border-color)] text-[var(--muted-color)]'}`}
              onClick={()=>setActiveTab(tab)}
            >{tab==='add'?'Add Plan':'Modify Plan'}</button>
          </li>
        ))}
      </ul>

      <div className="w-full max-w-4xl">
        {activeTab==='add' && (
          <form onSubmit={handleAddPlan} className="flex flex-col space-y-4 bg-[var(--card-bg)] p-4 rounded shadow text-[var(--text-color)]">
            {/* Name & Description */}
            {['name','description','stripeProductId','stripePriceId'].map(field=> (
              <div key={field} className="flex flex-col md:flex-row md:items-center">
                <label htmlFor={field} className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4 capitalize">{field.replace(/([A-Z])/g,' $1')}:</label>
                <input
                  type={field.includes('Id')?'text':field==='description'?'text':'text'}
                  id={field} name={field}
                  value={newPlan[field]}
                  onChange={handleNewPlanChange}
                  className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3 bg-[var(--input-bg)] text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] transition-all"
                  required
                />
              </div>
            ))}
            {/* Role, DurationUnit, DurationCount, Currency, Price */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label htmlFor="role" className="mb-1">Role:</label>
                <select name="role" id="role" value={newPlan.role} onChange={handleNewPlanChange}
                  className="p-2 border border-[var(--border-color)] rounded bg-[var(--input-bg)] focus:ring-2 focus:ring-[var(--primary-color)] transition-all">
                  <option value="student">Student</option>
                  <option value="professor">Professor</option>
                  <option value="institutional">Institutional</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label htmlFor="durationUnit" className="mb-1">Duration Unit:</label>
                <select name="durationUnit" id="durationUnit" value={newPlan.durationUnit} onChange={handleNewPlanChange}
                  className="p-2 border border-[var(--border-color)] rounded bg-[var(--input-bg)] focus:ring-2 focus:ring-[var(--primary-color)] transition-all">
                  <option value="semester">Semester</option>
                  <option value="month">Month</option>
                  <option value="year">Year</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label htmlFor="durationCount" className="mb-1">Duration Count:</label>
                <input type="number" name="durationCount" id="durationCount" min="1"
                  value={newPlan.durationCount} onChange={handleNewPlanChange}
                  className="p-2 border border-[var(--border-color)] rounded bg-[var(--input-bg)] focus:ring-2 focus:ring-[var(--primary-color)] transition-all" />
              </div>
              <div className="flex flex-col">
                <label htmlFor="price" className="mb-1">Price:</label>
                <input type="number" name="price" id="price" step="0.01" min="0"
                  value={newPlan.price} onChange={handleNewPlanChange}
                  className="p-2 border border-[var(--border-color)] rounded bg-[var(--input-bg)] focus:ring-2 focus:ring-[var(--primary-color)] transition-all" />
              </div>
              <div className="flex flex-col">
                <label htmlFor="currency" className="mb-1">Currency:</label>
                <select name="currency" id="currency" value={newPlan.currency} onChange={handleNewPlanChange}
                  className="p-2 border border-[var(--border-color)] rounded bg-[var(--input-bg)] focus:ring-2 focus:ring-[var(--primary-color)] transition-all">
                  <option value="USD">USD</option>
                  <option value="INR">INR</option>
                </select>
              </div>
              <div className="flex items-center">
                <input type="checkbox" name="isActive" id="isActive" checked={newPlan.isActive} onChange={handleNewPlanChange} />
                <label htmlFor="isActive" className="ml-2">Active</label>
              </div>
            </div>
            {/* Features */}
            <div className="flex flex-col md:flex-row md:items-center">
              <label htmlFor="features" className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4">Features (comma-separated):</label>
              <input type="text" id="features" name="features" value={newPlan.features} onChange={handleNewPlanChange}
                className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3 bg-[var(--input-bg)] focus:ring-2 focus:ring-[var(--primary-color)] transition-all" />
            </div>
            <div className="flex justify-end">
              <button type="submit" className="px-4 py-2 bg-[var(--primary-color)] hover:bg-[var(--hover-color)] text-white rounded">Add Plan</button>
            </div>
          </form>
        )}

        {activeTab==='modify' && (
          <>
            <table className="min-w-full border border-[var(--border-color)] mt-2">
              <thead className="bg-[var(--alt-bg)]">
                <tr>
                  <th className="py-2 px-4 border-b border-[var(--border-color)] text-left">Name</th>
                  <th className="py-2 px-4 border-b border-[var(--border-color)] text-left">Role</th>
                  <th className="py-2 px-4 border-b border-[var(--border-color)] text-left">Duration</th>
                  <th className="py-2 px-4 border-b border-[var(--border-color)] text-left">Price</th>
                  <th className="py-2 px-4 border-b border-[var(--border-color)] text-left">Active</th>
                  <th className="py-2 px-4 border-b border-[var(--border-color)] text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {plans.length===0 ? (
                  <tr><td colSpan="6" className="py-2 px-4 text-center text-[var(--muted-color)]">No plans found.</td></tr>
                ) : plans.map(plan=> (
                  <tr key={plan.id} className="hover:bg-[var(--card-hover-bg)] transition-colors">
                    <td className="py-2 px-4 border-b border-[var(--border-color)]">{plan.name}</td>
                    <td className="py-2 px-4 border-b border-[var(--border-color)]">{plan.role}</td>
                    <td className="py-2 px-4 border-b border-[var(--border-color)]">{plan.durationCount} {plan.durationUnit}</td>
                    <td className="py-2 px-4 border-b border-[var(--border-color)]">{plan.price} {plan.currency}</td>
                    <td className="py-2 px-4 border-b border-[var(--border-color)]">{plan.isActive ? 'Yes':'No'}</td>
                    <td className="py-2 px-4 border-b border-[var(--border-color)]">
                      <button onClick={()=>handleEditClick(plan.id)} className="px-3 py-1 bg-[var(--primary-color)] hover:bg-[var(--hover-color)] text-white rounded text-sm">Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {selectedPlanId && (
              <form onSubmit={handleUpdatePlan} className="flex flex-col space-y-4 bg-[var(--card-bg)] p-4 rounded shadow text-[var(--text-color)] mt-4">
                <h3 className="text-xl font-bold">Edit Plan</h3>
                {/* Repeat fields similar to Add form but using planDetails and handlePlanDetailChange */}
                {['name','description','stripeProductId','stripePriceId'].map(f=> (
                  <div key={f} className="flex flex-col md:flex-row md:items-center">
                    <label htmlFor={f} className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4 capitalize">{f.replace(/([A-Z])/g,' $1')}:</label>
                    <input type="text" id={f} name={f} value={planDetails[f]} onChange={handlePlanDetailChange}
                      className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3 bg-[var(--input-bg)] focus:ring-2 focus:ring-[var(--primary-color)] transition-all" required />
                  </div>
                ))}
                {/* Role, DurationUnit, DurationCount, Currency, Price */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label htmlFor="role" className="mb-1">Role:</label>
                    <select name="role" id="role" value={planDetails.role} onChange={handlePlanDetailChange}
                      className="p-2 border border-[var(--border-color)] rounded focus:ring-2 focus:ring-[var(--primary-color)] transition-all">
                      <option value="student">Student</option>
                      <option value="professor">Professor</option>
                      <option value="institutional">Institutional</option>
                    </select>
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="durationUnit" className="mb-1">Duration Unit:</label>
                    <select name="durationUnit" id="durationUnit" value={planDetails.durationUnit} onChange={handlePlanDetailChange}
                      className="p-2 border border-[var(--border-color)] rounded focus:ring-2 focus:ring-[var(--primary-color)] transition-all">
                      <option value="semester">Semester</option>
                      <option value="month">Month</option>
                      <option value="year">Year</option>
                    </select>
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="durationCount" className="mb-1">Duration Count:</label>
                    <input type="number" name="durationCount" id="durationCount" min="1" value={planDetails.durationCount}
                      onChange={handlePlanDetailChange} className="p-2 border border-[var(--border-color)] rounded focus:ring-2 focus:ring-[var(--primary-color)] transition-all" />
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="price" className="mb-1">Price:</label>
                    <input type="number" name="price" id="price" step="0.01" min="0" value={planDetails.price}
                      onChange={handlePlanDetailChange} className="p-2 border border-[var(--border-color)] rounded focus:ring-2 focus:ring-[var(--primary-color)] transition-all" />
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="currency" className="mb-1">Currency:</label>
                    <select name="currency" id="currency" value={planDetails.currency} onChange={handlePlanDetailChange}
                      className="p-2 border border-[var(--border-color)] rounded focus:ring-2 focus:ring-[var(--primary-color)] transition-all">
                      <option value="USD">USD</option>
                      <option value="INR">INR</option>
                    </select>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" name="isActive" id="isActive" checked={planDetails.isActive} onChange={handlePlanDetailChange} />
                    <label htmlFor="isActive" className="ml-2">Active</label>
                  </div>
                </div>
                <div className="flex flex-col md:flex-row md:items-center">
                  <label htmlFor="features" className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4">Features (comma-separated):</label>
                  <input type="text" id="features" name="features" value={planDetails.features} onChange={handlePlanDetailChange}
                    className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3 focus:ring-2 focus:ring-[var(--primary-color)] transition-all" />
                </div>
                <div className="flex justify-end">
                  <button type="submit" className="px-4 py-2 bg-[var(--primary-color)] hover:bg-[var(--hover-color)] text-white rounded">Save Changes</button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default Plans;
